import styled from "@emotion/styled";
import React, { HTMLAttributes, useState } from "react";
import Tooltip from "./Tooltip";
import arrowIcon from "../assets/icons/arrowRightBorderless.svg";
import check from "../assets/icons/checkMark.svg";
import SizedBox from "./SizedBox";
import { Column } from "./Flex";

export interface IOption {
  key: string;
  title: string;
}

interface IProps extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  options: IOption[];
  selected?: IOption;
  onSelect: (key: IOption) => void;
  minWidth?: string;
}

const Root = styled.div<{ focused?: boolean }>`
  display: flex;
  padding: 4px 4px 4px 10px;
  border-radius: 6px;
  background: ${({ focused }) => (focused ? "#ffffff" : "#F1F2FE")};
  border: 1px solid ${({ focused }) => (focused ? "#7075e9" : "#f1f2fe")};
  outline: none;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #363870;
  align-items: center;
  white-space: nowrap;

  .menu-arrow {
    transition: 0.4s;
    transform: ${({ focused }) =>
      focused ? "rotate(-90deg)" : "rotate(90deg)"};
  }

  @media (min-width: 480px) {
    padding: 8px 8px 8px 12px;
    font-size: 16px;
    border-radius: 10px;
  }
`;
const Option = styled.div<{ active?: boolean }>`
  width: 100%;
  display: flex;
  cursor: pointer;
  position: relative;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ active }) => (active ? "#3638708F" : "#363870")};
  padding: 10px 12px 10px 22px;
  background: #ffffff;
  margin: 0 -16px;
  white-space: nowrap;

  :hover {
    background: #f1f2fe;
  }

  ::after {
    transition: 0.4s;
    position: absolute;
    right: 12px;
    ${({ active }) => active && `content: url(${check})`};
  }
`;

const Select: React.FC<IProps> = ({ options, selected, onSelect, minWidth, ...rest }) => {
  const [focused, setFocused] = useState(false);
  return (
    <Tooltip
      config={{
        placement: "bottom-start",
        trigger: "click",
        onVisibleChange: setFocused
      }}
      minWidth={minWidth}
      content={
        <Column crossAxisSize="max">
          {options.map((v) => {
            const active = selected?.key === v.key;
            return (
              <Option
                active={active}
                key={v.key + "_option"}
                onClick={() => {
                  onSelect(v);
                }}
              >
                {v.title}
              </Option>
            );
          })}
        </Column>
      }
    >
      <Root
        focused={focused}
        onClick={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...rest}
      >
        {selected?.title ?? options[0].title}
        <SizedBox width={8} />
        <img src={arrowIcon} className="menu-arrow" alt="arrow" />
      </Root>
    </Tooltip>
  );
};
export default Select;
